import React, { useEffect, useState } from 'react';
import { API } from '../api';
import SidePanel from '../ui/SidePanel';
import Button from '../ui/Button';
import { Field, controlClass } from '../ui/Field';
import { useToast } from '../ui/Toast';
import { formatCount, plural } from '../ui/format';

// key: request field; column: the API row field; options: key in GET_OWNERSHIP_OPTIONS.
const OWNERSHIP_FIELDS = [
  { key: 'team', column: 'team_flow', label: 'Team', options: 'teams' },
  { key: 'project', column: 'team_project', label: 'Project', options: 'projects' },
  { key: 'admin', column: 'host_admin', label: 'Admin', options: 'admins', hint: 'Tickets go to the admin. Hosts without one get no tickets.' },
  { key: 'region', column: 'region', label: 'Region', options: 'regions' },
];

const EMPTY_VALUES = { team: '', project: '', admin: '', region: '' };
const NOT_CLEARED = { team: false, project: false, admin: false, region: false };

const parseHostnames = (text) => [...new Set(text.split(/[\s,]+/).map((h) => h.trim()).filter(Boolean))];

// "Now: …" for one field across the hosts whose rows are known; null when any row is unknown.
function currentValue(rows, column) {
  if (rows.length === 0 || rows.some((r) => !r)) return null;
  const values = new Set(rows.map((r) => String(r[column] || '').trim()));
  if (values.size > 1) return `${formatCount(values.size)} different values`;
  return [...values][0] || 'not set';
}

function OwnershipField({ field, value, cleared, now, options, disabled, onValue, onToggleClear }) {
  const id = `ownership-${field.key}`;
  return (
    <div>
      <div className="flex items-baseline gap-3 mb-1">
        <label htmlFor={id} className="text-small font-semibold text-ink">{field.label}</label>
        {now && <span className="ml-auto min-w-0 truncate text-small text-graphite" title={`Now: ${now}`}>Now: {now}</span>}
      </div>
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          list={options.length > 0 ? `${id}-options` : undefined}
          value={cleared ? '' : value}
          onChange={(e) => onValue(e.target.value)}
          placeholder={cleared ? 'Will be cleared' : 'Keep current'}
          autoComplete="off"
          spellCheck={false}
          disabled={disabled || cleared}
          aria-describedby={field.hint ? `${id}-hint` : undefined}
          className={`${controlClass()} ${cleared ? 'placeholder:text-med' : 'placeholder:text-graphite/70'}`}
        />
        <Button onClick={onToggleClear} disabled={disabled} aria-pressed={cleared} className="w-[92px]">
          {cleared ? 'Undo clear' : 'Clear'}
        </Button>
      </div>
      {options.length > 0 && (
        <datalist id={`${id}-options`}>
          {options.map((o) => <option key={o} value={o} />)}
        </datalist>
      )}
      {field.hint && <p id={`${id}-hint`} className="mt-1 text-small text-graphite">{field.hint}</p>}
    </div>
  );
}

function HostList({ hostnames }) {
  if (hostnames.length === 1) {
    return (
      <div>
        <p className="mb-1 text-small font-semibold text-ink">Host</p>
        <p className="font-mono text-id font-semibold text-ink break-all">{hostnames[0]}</p>
      </div>
    );
  }
  return (
    <div>
      <p className="mb-1.5 text-small font-semibold text-ink">{plural(hostnames.length, 'host')}</p>
      <ul className="max-h-[108px] overflow-y-auto flex flex-wrap gap-1" aria-label="Hosts to update">
        {hostnames.map((h) => (
          <li key={h} className="h-6 px-1.5 inline-flex items-center rounded-badge border border-rule bg-paper font-mono text-small text-ink">{h}</li>
        ))}
      </ul>
    </div>
  );
}

const describe = (change) => (change.clear ? `${change.label.toLowerCase()} cleared` : `${change.label.toLowerCase()} set to ${change.value}`);

/**
 * Side panel that edits the ownership of one or more hosts.
 * hosts: hostnames to update, or null to let the user type host names.
 * rows: { [hostname]: ownership row } for the hosts already loaded, used for the "Now:" values.
 * onSaved({ hostnames }) runs after a successful update; the panel shows the toast itself.
 */
export default function EditOwnershipPanel({ open, hosts, rows = {}, options, onClose, onSaved }) {
  const toast = useToast();
  const [typed, setTyped] = useState('');
  const [values, setValues] = useState(EMPTY_VALUES);
  const [cleared, setCleared] = useState(NOT_CLEARED);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTyped('');
    setValues(EMPTY_VALUES);
    setCleared(NOT_CLEARED);
    setErrors({});
    setSubmitError(null);
    setConfirming(false);
  }, [open, hosts]);

  useEffect(() => {
    if (confirming) document.getElementById('ownership-confirm')?.focus();
  }, [confirming]);

  const typing = !hosts;
  const hostnames = typing ? parseHostnames(typed) : hosts;
  const known = hostnames.map((h) => rows[h]);
  const changes = OWNERSHIP_FIELDS.flatMap((f) => {
    if (cleared[f.key]) return [{ ...f, clear: true, value: '' }];
    const value = values[f.key].trim();
    return value ? [{ ...f, clear: false, value }] : [];
  });
  const allCleared = OWNERSHIP_FIELDS.every((f) => cleared[f.key]);

  const edit = (fn) => {
    setConfirming(false);
    setSubmitError(null);
    setErrors((prev) => ({ ...prev, fields: undefined }));
    fn();
  };
  const setValue = (key) => (value) => edit(() => setValues((prev) => ({ ...prev, [key]: value })));
  const toggleClear = (key) => () => edit(() => setCleared((prev) => ({ ...prev, [key]: !prev[key] })));
  const clearAll = () => edit(() => setCleared(Object.fromEntries(OWNERSHIP_FIELDS.map((f) => [f.key, !allCleared]))));

  const save = async () => {
    setBusy(true);
    setSubmitError(null);
    const payload = Object.fromEntries(changes.map((c) => [c.key, c.value]));
    try {
      const text = await API.UPDATE_HOST_OWNERSHIP({ hostName: hostnames.join(', '), ...payload });
      let updated = null;
      try {
        updated = JSON.parse(text)?.updated_count ?? null;
      } catch (_err) {
        // older API versions answered with plain text
      }
      const subject = hostnames.length === 1 ? hostnames[0] : plural(hostnames.length, 'host');
      const summary = changes.map(describe).join(', ');
      if (typeof updated === 'number' && updated < hostnames.length) {
        const missed = hostnames.length - updated;
        toast.info(`Updated ${formatCount(updated)} of ${plural(hostnames.length, 'host')}: ${summary}. ${plural(missed, 'host name')} matched no host; check the spelling.`);
      } else {
        toast.success(`Updated ${subject}: ${summary}.`);
      }
      onSaved?.({ hostnames });
      onClose();
    } catch (error) {
      setConfirming(false);
      setSubmitError(error.message || 'The request did not complete.');
    } finally {
      setBusy(false);
    }
  };

  const submit = (event) => {
    event?.preventDefault();
    const found = {};
    if (hostnames.length === 0) found.hosts = 'Enter at least one host name.';
    if (changes.length === 0) found.fields = 'Enter a value, or clear at least one field.';
    setErrors(found);
    if (found.hosts) {
      document.getElementById('ownership-hosts')?.focus();
      return;
    }
    if (found.fields) return;
    if (hostnames.length > 1 && !confirming) {
      setConfirming(true);
      return;
    }
    save();
  };

  const updateLabel = hostnames.length > 1 ? `Update ${plural(hostnames.length, 'host')}` : 'Update';

  // Keys keep React from reusing the clicked Back button as the submit button within the same click.
  const footer = confirming ? (
    <>
      <p key="prompt" className="mr-auto self-center text-body text-ink">Change {plural(hostnames.length, 'host')}?</p>
      <Button key="back" onClick={() => setConfirming(false)} disabled={busy}>Back</Button>
      <Button key="confirm" id="ownership-confirm" variant="primary" onClick={save} disabled={busy}>
        {busy ? 'Updating…' : 'Confirm update'}
      </Button>
    </>
  ) : (
    <>
      <Button key="cancel" onClick={onClose} disabled={busy}>Cancel</Button>
      <Button key="submit" type="submit" form="edit-ownership-form" variant="primary" disabled={busy}>
        {busy ? 'Updating…' : updateLabel}
      </Button>
    </>
  );

  return (
    <SidePanel
      open={open}
      onClose={busy ? () => {} : onClose}
      title="Edit ownership"
      description="Fill in the fields to change. Empty fields keep their current value."
      footer={footer}
    >
      <form id="edit-ownership-form" onSubmit={submit} noValidate className="space-y-5">
        {typing ? (
          <Field
            id="ownership-hosts"
            label="Host names"
            hint={hostnames.length > 0 ? `${plural(hostnames.length, 'host name')}. Names must match exactly.` : 'Exact host names, separated by commas, spaces or new lines.'}
            error={errors.hosts}
          >
            <textarea
              id="ownership-hosts"
              rows={3}
              value={typed}
              onChange={(e) => {
                setTyped(e.target.value);
                setConfirming(false);
                setErrors((prev) => ({ ...prev, hosts: undefined }));
              }}
              placeholder="app-server-1, db-server-1"
              autoComplete="off"
              spellCheck={false}
              disabled={busy}
              aria-invalid={Boolean(errors.hosts)}
              aria-describedby={errors.hosts ? 'ownership-hosts-error' : 'ownership-hosts-hint'}
              className={`${controlClass({ invalid: Boolean(errors.hosts), mono: true })} h-auto py-1.5 resize-y`}
            />
          </Field>
        ) : (
          <HostList hostnames={hostnames} />
        )}

        <fieldset disabled={busy || confirming} className="space-y-4 pt-4 border-t border-rule">
          <legend className="sr-only">Ownership fields</legend>
          {OWNERSHIP_FIELDS.map((field) => (
            <OwnershipField
              key={field.key}
              field={field}
              value={values[field.key]}
              cleared={cleared[field.key]}
              now={currentValue(known, field.column)}
              options={options?.[field.options] || []}
              disabled={busy || confirming}
              onValue={setValue(field.key)}
              onToggleClear={toggleClear(field.key)}
            />
          ))}
          <div className="flex items-center gap-3">
            <Button size="sm" variant="ghost" className="-ml-2.5 text-signal" onClick={clearAll}>
              {allCleared ? 'Undo clear all' : 'Clear all fields'}
            </Button>
            {errors.fields && <p role="alert" className="text-small text-crit">{errors.fields}</p>}
          </div>
        </fieldset>

        {changes.length > 0 && (
          <section aria-label="Changes" className="pt-4 border-t border-rule">
            <h3 className="mb-1.5 text-small font-semibold text-ink">
              {hostnames.length > 1 ? `Changes for ${plural(hostnames.length, 'host')}` : 'Changes'}
            </h3>
            <dl className="grid grid-cols-[88px_1fr] gap-y-1 text-body">
              {changes.map((c) => (
                <React.Fragment key={c.key}>
                  <dt className="text-graphite">{c.label}</dt>
                  <dd className="min-w-0 break-words">
                    {c.clear ? <span className="text-med font-semibold">Cleared</span> : <>Set to <span className="font-mono text-id font-semibold">{c.value}</span></>}
                  </dd>
                </React.Fragment>
              ))}
            </dl>
          </section>
        )}

        {submitError && (
          <div role="alert" className="px-3 py-2.5 rounded-control border border-crit/40 bg-crit-tint">
            <p className="text-body font-semibold text-crit">Ownership could not be updated</p>
            <p className="mt-1 font-mono text-small text-ink break-words">{submitError}</p>
          </div>
        )}
      </form>
    </SidePanel>
  );
}
